import { useCallback } from "react";
import { listConnectors } from "../../../api/connectorsApi";
import type { RouteOptionListing } from "../../../api/threads/routeOptions";
import { useLoad } from "../../../shared/loading/useLoad";
import { onlineConnectors } from "../hosting/routeChoices";
import type { ConnectorOptions } from "./llmOptions";

/** The server's route options for one connector: a Thread's (`listRouteOptions`) or a start's (`listStartRouteOptions`). */
export type RouteOptionsOf = (connectorId: string, signal: AbortSignal) => Promise<RouteOptionListing>;

/**
 * What the LLM picker and the form or card around it choose from (F106): each PC's latest
 * report and the server's route options for it, read together, so Refresh renews both and
 * the picker and its owner judge an LLM by the same answer. `connectorIds`: those PCs, or
 * `"online"` for every PC of the person's that is online now. `optionsOf` must keep its
 * identity while its question stays the same (`useCallback`).
 */
export function useLlmChoices(connectorIds: readonly string[] | "online", optionsOf: RouteOptionsOf) {
  const ids = connectorIds === "online" ? connectorIds : connectorIds.join(",");
  const load = useCallback(
    async (signal: AbortSignal): Promise<readonly ConnectorOptions[]> => {
      const named = ids === "online" || ids === "" ? [] : ids.split(",");
      const [connectors, ...namedListings] = await Promise.all([listConnectors(signal), ...named.map((id) => optionsOf(id, signal))]);
      const wanted = ids === "online" ? onlineConnectors(connectors).map((connector) => connector.connectorId) : named;
      const listings = ids === "online" ? await Promise.all(wanted.map((id) => optionsOf(id, signal))) : namedListings;
      return wanted.flatMap((id, index) => {
        const connector = connectors.find((candidate) => candidate.connectorId === id);
        const listing = listings[index];
        return connector === undefined || listing === undefined ? [] : [{ connector, listing }];
      });
    },
    [ids, optionsOf],
  );
  return useLoad(load);
}
